import { searchReports } from '../../api/reports/api';
import { getFileInfo } from '../../api/file/file-api';
import { NavLinkBadgeStatus } from './NavLinkType';
import {
  ArchiveBox,
  ArticleOutlined,
  AssessmentOutlined,
  CalendarTodayOutlined,
  ChecklistOutlined,
  ContactMailOutlined,
  CreditCardOutlined,
  DescriptionOutlined,
  DrawOutlined,
  EqualizerOutlined,
  FilledActionCalculator,
  GppGoodOutlined,
  InsightsOutlined,
  IntegrationInstructionsOutlined,
  ListAltOutlined,
  ListOutlined,
  LockClockOutlined,
  ManageSearchOutlined,
  OrderManagementAllOrders,
  OrderManagementFilesInProcess,
  OrderManagementInvoices,
  OrderManagementOrderInfo,
  OtherHousesOutlined,
  PeopleAltOutlined,
  PersonOutlined,
  ReceiptOutlined,
  SecurityOutlined,
  SourceOutlined,
  TaskOutlined,
  UploadFileOutlined,
  VerifiedOutlined,
} from './LeftNavIcons';
import { NavLinkText } from './NavLinkTexts';
import { NavLinkType } from './NavLinkType';

export const FilesLinks: NavLinkType[] = [
  {
    linkText: NavLinkText.OrderInsights,
    path: '/files/:fileId/order-insights',
    icon: <InsightsOutlined />,
    expanded: false,
    testId: 'GlobalNavFilesOrderInsights',
  },
  {
    linkText: NavLinkText.FileInfo,
    path: '/files/:fileId/file-info',
    icon: <ListOutlined />,
    expanded: false,
    testId: 'GlobalNavFiles',
  },
  {
    linkText: NavLinkText.BackTitle,
    path: '/files/:fileId/back-title',
    icon: <OtherHousesOutlined />,
    expanded: false,
    testId: 'GlobalNavFilesBackTitle',
  },
  {
    linkText: NavLinkText.CPL,
    path: '/files/:fileId/cpl',
    icon: <ArticleOutlined />,
    expanded: false,
    testId: 'GlobalNavFilesCPL',
  },
  {
    linkText: NavLinkText.ServiceOrders,
    path: '/files/:fileId/service-orders',
    icon: <ChecklistOutlined />,
    expanded: false,
    testId: 'GlobalNavFilesServiceOrders',
  },
  {
    linkText: NavLinkText.Underwriting,
    path: '/files/:fileId/underwriting',
    icon: <GppGoodOutlined />,
    expanded: false,
    testId: 'GlobalNavFilesUnderwriting',
  },
  {
    linkText: NavLinkText.Jackets,
    path: '/files/:fileId/jackets',
    icon: <SourceOutlined />,
    expanded: false,
    testId: 'GlobalNavFilesJackets',
  },
  {
    linkText: NavLinkText.RatesFees,
    path: '#ratesfees', //opens a drawer
    icon: <FilledActionCalculator />,
    expanded: false,
    testId: 'GlobalNavFilesRatesFeesDrawer',
    badgeContentFunction: async (token: string, fileProps = { fileId: undefined }, setFileData) => {
      const { fileId = '' } = fileProps;
      if (fileId) {
        const fileApiData = await getFileInfo(fileProps?.fileId ?? '', token);

        // Update FileDataContext if setFileData is provided
        if (setFileData && fileApiData) {
          setFileData(fileApiData);
        }

        const { fileStatus } = fileApiData;

        const {
          hasRemittableCpl = false,
          hasActiveJacket = false,
          hasStandAloneEndorsement = false,
          isPricingValid = false,
          isRemitValid = false,
          hasOpenEndorsement = false,
        } = fileApiData.serviceStatus ?? {};

        const openRemittableProductsNotCalculated =
          (hasRemittableCpl || hasActiveJacket || hasStandAloneEndorsement || hasOpenEndorsement) &&
          (!isPricingValid || (isPricingValid && !isRemitValid));
        const openRemittableProductsCalculated =
          (hasRemittableCpl || hasActiveJacket || hasStandAloneEndorsement || hasOpenEndorsement) &&
          isPricingValid &&
          isRemitValid;
        const noRemittableProducts =
          !hasRemittableCpl && !hasActiveJacket && !hasStandAloneEndorsement && !hasOpenEndorsement && !isPricingValid;

        if (fileStatus?.toLowerCase() !== 'open' || noRemittableProducts) {
          return NavLinkBadgeStatus.Idle;
        } else if (openRemittableProductsCalculated) {
          return NavLinkBadgeStatus.Success;
        } else if (openRemittableProductsNotCalculated) {
          return NavLinkBadgeStatus.Warning;
        }
      } else {
        console.warn('Could not compute fileId');
      }
    },
  },
  {
    linkText: NavLinkText.Documents,
    path: '/files/:fileId/documents',
    icon: <DescriptionOutlined />,
    expanded: false,
    testId: 'GlobalNavFilesDocuments',
  },
];

const getReviewBadgeCount = async (
  token: string,
  { firmId }: { firmId?: string; [key: string]: unknown },
): Promise<number | undefined> => {
  if (!firmId) return undefined;
  const result = await searchReports(token, firmId, 'Review');
  const count = Array.isArray(result) ? result.length : 0;
  return count > 0 ? count : undefined;
};

export const AccountingLinks: NavLinkType[] = [
  {
    linkText: NavLinkText.ReportPay,
    path: '/accounting/remittance',
    icon: <AssessmentOutlined />,
    badgeContentFunction: getReviewBadgeCount,
    sublinks: [
      {
        linkText: NavLinkText.Review,
        path: '/accounting/remittance/review',
        testId: 'GlobalNavAccountingReportPayReview',
        notificationStatus: 'default',
        badgeContentFunction: getReviewBadgeCount,
      },
      {
        linkText: NavLinkText.Report,
        path: '/accounting/remittance/report',
        testId: 'GlobalNavAccountingReportPayReport',
      },
      { linkText: NavLinkText.Pay, path: '/accounting/remittance/pay', testId: 'GlobalNavAccountingReportPayPay' },
    ],
    expanded: true,
    testId: 'GlobalNavAccountingReportPay',
  },
  {
    linkText: NavLinkText.ServiceOrderInvoices,
    path: '/accounting/service-order-invoices',
    icon: <ReceiptOutlined />,
    sublinks: [
      {
        linkText: NavLinkText.Pay,
        path: '/accounting/service-order-invoices/pay',
        testId: 'GlobalNavAccountingServiceOrderInvoicesPay',
      },
      {
        linkText: NavLinkText.ViewInvoices,
        path: '/accounting/service-order-invoices/view-invoices',
        testId: 'GlobalNavAccountingServiceOrderInvoicesView',
      },
    ],
    expanded: true,
    testId: 'GlobalNavAccountingServiceOrderInvoices',
  },
  {
    linkText: NavLinkText.Statements,
    path: '/statements',
    icon: <ListAltOutlined />,
    expanded: false,
    testId: 'GlobalNavAccountingStatements',
  },
];

export const ServicesLinks: NavLinkType[] = [
  {
    linkText: NavLinkText.AgencyVerificationLetters,
    path: '/services/agency-verification-letters',
    icon: <VerifiedOutlined />,
    expanded: false,
    testId: 'GlobalNavServicesAVL',
  },
  {
    linkText: NavLinkText.BackTitleSearch,
    path: '/services/back-title-search',
    icon: <ManageSearchOutlined />,
    expanded: false,
    testId: 'GlobalNavServicesBackTitle',
  },
  {
    linkText: NavLinkText.FraudPrevention,
    path: '/services/fraud-prevention',
    icon: <LockClockOutlined />,
    expanded: true,
    testId: 'GlobalNavServicesFraudPrevention',
    sublinks: [
      {
        linkText: NavLinkText.PartyVerification,
        path: '/services/fraud-prevention/identity-verification',
        testId: 'GlobalNavServicesFraudPreventionIdentityVerification',
      },
      {
        linkText: NavLinkText.WireVerification,
        path: '/services/fraud-prevention/wire-verification',
        testId: 'GlobalNavServicesFraudPreventionWireVerification',
      },
    ],
  },
  {
    linkText: NavLinkText.PolicyUpload,
    path: '/services/policy-upload',
    icon: <UploadFileOutlined />,
    expanded: true,
    testId: 'GlobalNavServicesPolicyUpload',
    sublinks: [
      {
        linkText: NavLinkText.Upload,
        path: '/services/policy-upload/upload',
        testId: 'GlobalNavServicesPolicyUploadUpload',
      },
      {
        linkText: NavLinkText.History,
        path: '/services/policy-upload/history',
        testId: 'GlobalNavServicesPolicyUploadHistory',
      },
    ],
  },
  {
    linkText: NavLinkText.PropertyReports,
    path: '/services/property-reports',
    icon: <TaskOutlined />,
    expanded: false,
    testId: 'GlobalNavServicesPropertyReports',
  },
  {
    linkText: NavLinkText.SDNSearch,
    path: '/services/sdn-search',
    icon: <SecurityOutlined />,
    expanded: false,
    testId: 'GlobalNavServicesSDN',
  },
];

export const ReportsLinks: NavLinkType[] = [
  {
    linkText: NavLinkText.Reports,
    path: '/reporting',
    icon: <EqualizerOutlined />,
    expanded: false,
    testId: 'GlobalNavReports',
  },
];

export const AdminLinks: NavLinkType[] = [
  {
    category: 'User',
    linkText: NavLinkText.UserProfilePreferences,
    path: '/admin/user/profile',
    icon: <PersonOutlined />,
    sublinks: [
      { linkText: NavLinkText.Profile, path: '/admin/user/profile', testId: 'AdminNavUserProfile' },
      {
        linkText: NavLinkText.PreferredOffices,
        path: '/admin/user/preferred-offices',
        testId: 'AdminNavPreferredOffices',
      },
    ],
    expanded: true,
    testId: 'AdminNavUser',
  },
  {
    category: 'Firm',
    linkText: NavLinkText.AddressBook,
    path: '/admin/address-book',
    icon: <ContactMailOutlined />,
    expanded: false,
    testId: 'AdminNavAddress',
  },
  {
    category: 'Firm',
    linkText: NavLinkText.Billing,
    path: '/admin/billing',
    icon: <CreditCardOutlined />,
    expanded: true,
    testId: 'AdminNavBilling',
    sublinks: [
      {
        linkText: NavLinkText.PaymentMethods,
        path: '/admin/billing',
        testId: 'AdminNavPaymentMethods',
      },
      {
        linkText: NavLinkText.AutopayServices,
        path: '/admin/autopay-services',
        testId: 'AdminNavAutopayServices',
      },
    ],
  },
  {
    category: 'Firm',
    linkText: NavLinkText.AutoReporting,
    path: '/admin/auto-reporting',
    icon: <AssessmentOutlined />,
    expanded: false,
    testId: 'AdminNavAutoReporting',
  },
  {
    category: 'Firm',
    linkText: NavLinkText.ESignatures,
    path: '/admin/e-signatures',
    icon: <DrawOutlined />,
    expanded: false,
    testId: 'AdminNavSignatures',
  },
  {
    category: 'Firm',
    linkText: NavLinkText.Users,
    path: '/admin/users/manage',
    icon: <PeopleAltOutlined />,
    expanded: false,
    testId: 'AdminNavUsers',
  },
  {
    category: 'Firm',
    linkText: NavLinkText.AddOns,
    path: '/admin/integrations-features',
    icon: <IntegrationInstructionsOutlined />,
    expanded: false,
    testId: 'AdminNavAddOns',
  },
  {
    category: 'Staff',
    linkText: NavLinkText.InventoryManagement,
    path: '/admin/inventory-management',
    icon: <ArchiveBox />,
    expanded: true,
    testId: 'AdminNavInventoryManagement',
    sublinks: [
      {
        linkText: NavLinkText.PolicyNumberManagement,
        path: '/admin/policy-number-management',
        testId: 'AdminNavPolicyNumberManagement',
      },
      {
        linkText: NavLinkText.FormManagement,
        path: '/admin/form-management',
        testId: 'AdminNavFormManagement',
      },
    ],
  },
  {
    category: 'Staff',
    linkText: NavLinkText.OracleDowntimeSchedule,
    path: '/admin/oracle-downtime-schedule',
    icon: <CalendarTodayOutlined />,
    expanded: false,
    testId: 'AdminNavOracleDowntimeSchedule',
  },
  {
    category: 'Staff',
    linkText: NavLinkText.Invoices,
    path: '/admin/invoices',
    icon: <OrderManagementInvoices />,
    expanded: false,
    testId: 'AdminNavInvoices',
  },
];

export const DashboardLinks: NavLinkType[] = [
  {
    linkText: NavLinkText.ServiceOrders,
    path: '/dashboards/service-orders',
    icon: <ChecklistOutlined />,
    expanded: false,
    testId: 'GlobalNavServicesOrders',
  },
];

export const OrderManagementInfoPanelLinks: NavLinkType[] = [
  {
    linkText: NavLinkText.OrderManagementDashboard,
    path: '',
    icon: <OrderManagementOrderInfo />,
    expanded: false,
    testId: 'OrderManagementDashboard',
    notification: 'dot',
    badgeContentFunction: (
      _token: string,
      { selectedFilesAmount }: { selectedFilesAmount?: { netDifference: number }; [key: string]: unknown },
    ) => {
      return Promise.resolve(
        selectedFilesAmount?.netDifference !== 0 ? NavLinkBadgeStatus.Warning : NavLinkBadgeStatus.Success,
      );
    },
  },
];

export const OrderManagementLinks: NavLinkType[] = [
  {
    linkText: NavLinkText.AllOrders,
    path: '/order-management/all-orders',
    icon: <OrderManagementAllOrders />,
    expanded: false,
    testId: 'AllOrdersTestLink',
  },
  {
    linkText: NavLinkText.FilesInProcess,
    path: '/order-management/files-in-process',
    icon: <OrderManagementFilesInProcess />,
    expanded: false,
    testId: 'FilesInProcessTestLink',
  },
];

export const getFincenLinks = (showSummary = false): NavLinkType[] => [
  {
    linkText: NavLinkText.FinCenRealEstateReport,
    path: '/files/:fileId/fincen-reporting/filing-information',
    icon: <VerifiedOutlined />,
    expanded: true,
    testId: 'fincen-nav-reporting',
    sublinks: [
      {
        linkText: NavLinkText.FilingInformation,
        path: '/files/:fileId/fincen-reporting/filing-information',
        testId: 'fincen-nav-filing-information',
      },
      {
        linkText: NavLinkText.ReportingPartyInformation,
        path: '/files/:fileId/fincen-reporting/report-party',
        testId: 'fincen-nav-report-party',
      },
      {
        linkText: NavLinkText.PropertyInformation,
        path: '/files/:fileId/fincen-reporting/property-info',
        testId: 'fincen-nav-property-info',
      },
      {
        linkText: NavLinkText.TransfereeInformation,
        path: '/files/:fileId/fincen-reporting/transferee-info',
        testId: 'fincen-nav-transferee-info',
      },
      {
        linkText: NavLinkText.TransferorInformation,
        path: '/files/:fileId/fincen-reporting/transferor-info',
        testId: 'fincen-nav-transferor-info',
      },
      {
        linkText: NavLinkText.PaymentInformation,
        path: '/files/:fileId/fincen-reporting/payment-info',
        testId: 'fincen-nav-payment-info',
      },
      {
        linkText: NavLinkText.CertifyGenerateReport,
        path: '/files/:fileId/fincen-reporting/certify-generate',
        testId: 'fincen-nav-certify-generate',
      },
      ...(showSummary
        ? [
            {
              linkText: NavLinkText.Summary,
              path: '/files/:fileId/fincen-reporting/summary',
              testId: 'fincen-nav-summary',
            },
          ]
        : []),
    ],
  },
];

export const FincenLinks: NavLinkType[] = getFincenLinks(false);
